import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAllTenantProfiles } from "../../api/tenantProfileApi";
import PageHeader from "./PageHeader";
import LoadingState from "./LoadingState";
import ErrorState from "./ErrorState";
import EmptyState from "./EmptyState";

const TenantProfilesList = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPublic, setFilterPublic] = useState("ALL"); // "ALL", "PUBLIC", "HIDDEN"

  const loadProfiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAllTenantProfiles();
      const list = res?.data || res || [];
      if (Array.isArray(list)) {
        setProfiles(list);
      } else {
        setProfiles([]);
      }
    } catch (err) {
      console.error("Failed to fetch tenant profiles list:", err);
      setError(err?.message || "Failed to load tenant profiles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfiles();
  }, []);

  const filteredProfiles = profiles.filter((tenant) => {
    const heading = (tenant.heading || "").toLowerCase();
    const tenantId = (tenant.tenant_id || tenant.id || "").toLowerCase();
    const query = searchQuery.toLowerCase().trim();

    const matchesSearch = heading.includes(query) || tenantId.includes(query);

    if (!matchesSearch) return false;

    if (filterPublic === "PUBLIC") return tenant.is_profile_public === true;
    if (filterPublic === "HIDDEN") return !tenant.is_profile_public;
    return true;
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tenant Profiles Directory"
        description="Manage and review public & internal business profiles for all onboarded tenants."
      />

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search by heading or tenant ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#096dea] focus:border-transparent"
          />
          <svg
            className="absolute left-3 top-2.5 h-4 w-4 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Visibility:</span>
          <button
            onClick={() => setFilterPublic("ALL")}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
              filterPublic === "ALL"
                ? "bg-[#096dea] text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            All ({profiles.length})
          </button>
          <button
            onClick={() => setFilterPublic("PUBLIC")}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
              filterPublic === "PUBLIC"
                ? "bg-emerald-600 text-white"
                : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            }`}
          >
            Public ({profiles.filter((p) => p.is_profile_public).length})
          </button>
          <button
            onClick={() => setFilterPublic("HIDDEN")}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
              filterPublic === "HIDDEN"
                ? "bg-slate-700 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            Hidden ({profiles.filter((p) => !p.is_profile_public).length})
          </button>
        </div>
      </div>

      {/* State Rendering */}
      {loading ? (
        <LoadingState message="Loading tenant profiles..." />
      ) : error ? (
        <ErrorState message={error} onRetry={loadProfiles} />
      ) : filteredProfiles.length === 0 ? (
        <EmptyState
          title="No tenant profiles found"
          description={
            searchQuery
              ? `No profiles matched "${searchQuery}"`
              : "No tenant profiles available."
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredProfiles.map((tenant) => {
            const logoUrl = tenant.logo_data
              ? `data:${tenant.logo_mime_type || "image/png"};base64,${tenant.logo_data}`
              : null;
            const tenantId = tenant.tenant_id || tenant.id;

            return (
              <div
                key={tenantId}
                className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt={tenant.heading || tenantId}
                          className="w-12 h-12 rounded-xl object-contain border border-slate-200 bg-slate-50 p-1"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center font-bold text-slate-500 text-lg">
                          {(tenant.heading || tenantId || "T").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <h3 className="font-bold text-slate-900 text-base line-clamp-1">
                          {tenant.heading || tenantId}
                        </h3>
                        <p className="text-xs text-slate-400 font-mono">
                          ID: {tenantId}
                        </p>
                      </div>
                    </div>

                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        tenant.is_profile_public
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-slate-100 text-slate-600 border border-slate-200"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          tenant.is_profile_public ? "bg-emerald-500" : "bg-slate-400"
                        }`}
                      ></span>
                      {tenant.is_profile_public ? "Public" : "Hidden"}
                    </span>
                  </div>

                  <p className="text-xs text-slate-600 leading-relaxed line-clamp-3 mb-4 min-h-[3rem]">
                    {tenant.description || "No description provided."}
                  </p>
                </div>

                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                  <Link
                    to={`/admin/tenants/${tenantId}`}
                    className="font-medium text-[#096dea] hover:underline"
                  >
                    View Details →
                  </Link>

                  {tenant.is_profile_public && (
                    <Link
                      to={`/public/tenants/${tenantId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-500 hover:text-slate-900 flex items-center gap-1"
                    >
                      Public View ↗
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TenantProfilesList;
